import { Context } from "chartjs-plugin-datalabels";
import { floatingLabels } from "../../utils/chartjsPlugins";
import { Paper } from "@mui/material";
import { experimentalStyled as styled } from "@mui/material/styles";
import { Bar } from "react-chartjs-2";
import { Type1Question } from "../../models/Question";
import { scoreInterpolationColor } from "../../utils/scoreColor";
import { useContext } from "react";
import "./chart-responsive.css";
import { ChartsContext } from "../ChartsContext";

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: '16px',
  paddingLeft: '30px',
  marginBottom: theme.spacing(3),
  color: theme.palette.text.primary,
  width: "100%",
  height: "310px",
  display: "flex",
  flexDirection: "column",
  border: '1px solid #e0e0e0'
}));


interface Props {
  index: number;
  question: Type1Question;
}

export function Type1QuestionChart({ index, question }: Props): JSX.Element {
  const { survey } = useContext(ChartsContext);
  const hasTemplate = survey?.templateName && survey.templateName.trim() !== "";

  // Función para truncar textos largos
  const truncateLabel = (label: string, maxLength: number = 35) => {
    if (label.length > maxLength) {
      return label.substring(0, maxLength) + '...';
    }
    return label;
  };

  // let chartConfig = new RatingChartConfig(question);
  // let { options, data } = chartConfig.createChartConfig();
  // options.plugins = { legend: { display: false } };
  // // options.maintainAspectRatio = false;

  /*
  const data = {
    labels: ["Red", "Blue", "Yellow", "Green", "Purple", "Orange"],
    datasets: [
      {
        label: "# of Votes",
        data: [12, 19, 3, 5, 2, 3],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };
  */

  // Decidir qué colores usar según si es template o custom
  let colors;
  
  if (hasTemplate) {
    // Surveys con template: usar la escala de colores original basada en sentimientos
    colors = question.choices.map((value, index) =>
      scoreInterpolationColor(index, question.maxScore)
    );
  } else {
    // Custom surveys: usar paleta de colores vibrantes (evitando colores de sentimientos)
    const vibrantColors = [
      '#1E88E5', // Azul vibrante
      '#8E24AA', // Morado
      '#00ACC1', // Cian
      '#5E35B1', // Morado oscuro
      '#039BE5', // Azul cielo
      '#00897B', // Verde azulado (teal)
      '#3949AB', // Índigo
      '#EC407A', // Rosa
      '#26C6DA', // Cian claro
      '#7E57C2', // Morado medio
      '#42A5F5', // Azul claro
      '#AB47BC', // Magenta
    ];
    
    colors = question.choices.map((value, index) => {
      return vibrantColors[index % vibrantColors.length];
    });
  }

  const data = {
    labels: question.choices.map(choice => truncateLabel(choice)),
    datasets: [
      {
        label: "label",
        data: question.choiceValues,
        borderWidth: 1,
        backgroundColor: colors,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          align: 'start' as const,
          crossAlign: 'far' as const,
        }
      },
    },
    indexAxis: "y" as const,

    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: (context: any) => {
            // Mostrar el texto completo en el tooltip
            const index = context[0].dataIndex;
            return question.choices[index];
          },
          label: (context: any) => {
            // Mostrar solo el valor sin "label:"
            return `Respuestas: ${context.parsed.x}`;
          }
        }
      },
      datalabels: {
        color: "#111",
        textAlign: "center",
        font: {
          lineHeight: 1.6,
        },
        formatter: (value: number, ctx: Context) => {
          if (ctx.chart.data.labels && value > 0) {
            return (
              (value /
                (question.choiceValues.reduce(
                  (previousValue, currentValue) => previousValue + currentValue
                )) *
                100).toFixed(2) +
              "%"
            );
          } else if (value === 0) {
            return "";
          }
        },
      },
    },
  };

  // console.log("choices", question.choices);
  // console.log("choiceValues", question.choiceValues);
  // console.log("colors", colors);

  return (
    <Item sx={{ height: { xs: '250px', sm: '280px', md: '310px' } }}>
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "500",
          color: "#4E4E4E",
          fontFamily: "'IBM Plex Sans', sans-serif",
          margin: 0,
          marginBottom: "16px"
        }}
      >
        {index}. {question.title}
      </h3>
      <div className="chart-container" style={{flex: 1, minHeight: 0, position: 'relative'}}>
        <Bar
          // @ts-ignore
          options={options}
          data={data}
          plugins={[floatingLabels]}
        />
      </div>
    </Item>
  );
}
